// SPDX-License-Identifier: MIT
// Cœur PUR du sous-système plan-usage : agrège des UsageEvent en WindowState.
// Aucune I/O, aucune dépendance Tauri/edge — entièrement unit-testable.
import {
  type UsageEvent, type WindowState, type PlanTier, type Confidence, type UsageSource,
  WINDOW_DURATION_MS, APPROACHING_MINS, APPROACHING_PCT, billableTokens,
} from './usage-types';
import { capTokensFor } from './plan-caps';

export interface ComputeOpts {
  planTier: PlanTier;
  nowMs: number;        // injecté (jamais Date.now() interne → testable)
  source?: UsageSource; // défaut dérivé des events
  // Plafond calibré au throttle réel (écrase la graine statique du tier). Quand
  // fourni et fini, il sert de cap pour fillPct + projection — la graine ne
  // reflète plus la réalité une fois qu'on a observé un vrai throttle.
  calibratedCap?: number | null;
}

export function computeWindowState(events: UsageEvent[], opts: ComputeOpts): WindowState {
  const rateLimits = events.filter((e): e is Extract<UsageEvent, { kind: 'rate_limit' }> => e.kind === 'rate_limit');
  const webHeaders = events.filter((e): e is Extract<UsageEvent, { kind: 'web_headers' }> => e.kind === 'web_headers');
  const usages = events.filter((e): e is Extract<UsageEvent, { kind: 'usage' }> => e.kind === 'usage');

  const lastRl = rateLimits.length ? rateLimits[rateLimits.length - 1] : null;
  const lastWeb = webHeaders.length ? webHeaders[webHeaders.length - 1] : null;

  // Ancrage de la fenêtre : resetsAt autoritatif > web resetsAt > heuristique.
  let resetsAt: number | null = null;
  let confidence: Confidence = 'estimated';
  if (lastRl) { resetsAt = lastRl.resetsAt; confidence = 'authoritative'; }
  else if (lastWeb && lastWeb.resetsAt) { resetsAt = lastWeb.resetsAt; confidence = 'estimated'; }
  else if (usages.length) { resetsAt = Math.floor((minTs(usages) + WINDOW_DURATION_MS) / 1000); confidence = 'estimated'; }

  const source: UsageSource = opts.source ?? (lastWeb && !lastRl && !usages.length ? 'web-headers' : lastRl ? 'stream' : usages.length ? 'jsonl' : 'stream');

  const windowStartMs = resetsAt != null ? resetsAt * 1000 - WINDOW_DURATION_MS : -Infinity;
  const inWindow = usages.filter((u) => u.timestampMs >= windowStartMs && u.timestampMs <= opts.nowMs);
  const windowTokens = inWindow.reduce((acc, u) => acc + billableTokens(u), 0);

  const burnRatePerMin = computeBurnRate(inWindow, opts.nowMs);
  // Calibrated cap (learned at a real throttle) wins over the static seed.
  const cap = (opts.calibratedCap != null && Number.isFinite(opts.calibratedCap) && opts.calibratedCap > 0)
    ? opts.calibratedCap
    : capTokensFor(opts.planTier);
  const fillPct = cap != null && cap > 0 ? Math.min(100, Math.round((windowTokens / cap) * 100)) : null;
  const projectionMins = projection(cap, windowTokens, burnRatePerMin);

  let status: WindowState['status'] = 'ok';
  if (lastRl?.status === 'throttled' || lastWeb?.status === 'throttled') status = 'throttled';
  else if ((projectionMins != null && projectionMins < APPROACHING_MINS) || (fillPct != null && fillPct >= APPROACHING_PCT)) status = 'approaching';

  return { planTier: opts.planTier, status, resetsAt, fillPct, burnRatePerMin, projectionMins, confidence, source, windowTokens };
}

function minTs(us: Array<Extract<UsageEvent, { kind: 'usage' }>>): number {
  return us.reduce((m, u) => Math.min(m, u.timestampMs), Infinity);
}

function computeBurnRate(inWindow: Array<Extract<UsageEvent, { kind: 'usage' }>>, nowMs: number): number | null {
  if (inWindow.length < 2) return null;
  const first = inWindow.reduce((m, u) => Math.min(m, u.timestampMs), Infinity);
  const elapsedMin = (nowMs - first) / 60_000;
  if (elapsedMin <= 0) return null;
  const tokens = inWindow.reduce((a, u) => a + billableTokens(u), 0);
  return Math.round(tokens / elapsedMin);
}

function projection(cap: number | null, used: number, burn: number | null): number | null {
  if (cap == null || burn == null || burn <= 0) return null;
  const remaining = cap - used;
  if (remaining <= 0) return 0;
  return Math.round(remaining / burn);
}
